import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useMyApprovalStatus } from "@/hooks/useMyApprovalStatus";
import {
  isGatedByInterstitial,
  useInterstitialStatus,
} from "@/hooks/useInterstitialStatus";
import { ApprovalPending } from "./ApprovalPending";
import { AccountDeactivated } from "./AccountDeactivated";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const { status: approvalStatus, isLoading: approvalLoading } = useMyApprovalStatus();
  const { data: interstitialStatus, isLoading: interstitialLoading } =
    useInterstitialStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const onWelcome = location.pathname === "/welcome";

  // Imperative redirects via useEffect (not <Navigate>) - react-router-dom v6's
  // <Navigate> runs its internal effect on every render with no deps array.
  // Combined with AnimatePresence mode="wait" in AnimatedRoutes (which keeps
  // the exiting route mounted through its 350ms transition) plus React Query
  // state churn from useMyApprovalStatus / useInterstitialStatus, that fires
  // history.replaceState() dozens of times in a row and trips Chrome's
  // "more than 100 times per 10 seconds" throttle, throwing into Sentry's
  // error boundary. Effects with proper deps fire exactly once per gate flip.
  const needsAuthRedirect = !loading && !user;
  const needsWelcomeRedirect =
    !loading &&
    !!user &&
    !!user.email_confirmed_at &&
    !approvalLoading &&
    approvalStatus === "approved" &&
    !interstitialLoading &&
    isGatedByInterstitial(interstitialStatus) &&
    !onWelcome;

  useEffect(() => {
    if (needsAuthRedirect) {
      navigate("/login", { state: { from: location }, replace: true });
    }
    // location intentionally omitted - capturing the location at the moment
    // the redirect fires is fine, we don't want this to re-fire on every
    // location change while AnimatePresence keeps us mounted during exit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsAuthRedirect, navigate]);

  useEffect(() => {
    if (needsWelcomeRedirect) {
      navigate("/welcome", { replace: true });
    }
  }, [needsWelcomeRedirect, navigate]);

  if (loading) return <Spinner />;
  if (!user || needsAuthRedirect) return <Spinner />;
  if (!user.email_confirmed_at) {
    return <EmailVerificationPending email={user.email} />;
  }
  if (approvalLoading) return <Spinner />;
  if (approvalStatus === "deactivated") return <AccountDeactivated />;
  if (approvalStatus === "declined") return <ApprovalPending variant="declined" />;
  if (approvalStatus !== "approved") return <ApprovalPending variant="pending" />;
  if (interstitialLoading) return <Spinner />;
  if (needsWelcomeRedirect) return <Spinner />;

  return <>{children}</>;
};

function EmailVerificationPending({ email }: { email: string | undefined }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    try {
      await supabase.auth.resend({ type: "signup", email });
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center shadow-soft">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-primary" />
        </div>

        <h2 className="text-2xl font-serif font-semibold text-foreground mb-3">
          Verify Your Email
        </h2>

        <p className="text-muted-foreground mb-2">
          Please check your inbox and verify your email to access the community.
        </p>

        {email && (
          <p className="text-sm font-medium text-foreground mb-6">{email}</p>
        )}

        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={resending || resent}
          >
            {resending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : resent ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Verification email sent
              </>
            ) : (
              "Resend Verification Email"
            )}
          </Button>

          <Link
            to="/"
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
